/**
 * Performance Rating Cards Component
 *
 * Displays YTD and Month supplier rating with grade and statement.
 *
 * The grade sits in a coloured badge beside its statement instead of a
 * green title band / grade band / "STATEMENT" band / text band stack — four
 * rows to say one letter and one sentence.
 */
import { Card, CardContent } from '../ui/card'
import { getRatingColors } from './performanceConstants'
import { ratingBadgeStyle } from '../../pages/supplier-performance/utils'

interface RatingCardProps {
  title: string
  periodLabel: string
  rating: string
  colorKey: string
  /** Customer review 11 Sep (msg 2): the band's configured palette colour. */
  hex?: string | null
  statement: string
}

function RatingCard({ title, periodLabel, rating, colorKey, hex, statement }: RatingCardProps) {
  const colors = getRatingColors(colorKey)
  // The palette colour wins — the one the Supplier Performance table draws the
  // same band in. The colour-name map knew six traffic-light hexes and turned
  // every palette colour (Platinum, Gold, Silver, Bronze) grey.
  const fill = hex ? ratingBadgeStyle(hex) : undefined

  return (
    <Card className="shadow-sm">
      <CardContent className="p-4 flex items-center gap-4">
        <div
          className={`shrink-0 w-16 h-16 rounded-full border-2 ${fill ? '' : `${colors.bg} ${colors.text} ${colors.border}`} flex items-center justify-center`}
          style={fill}
          aria-label={`${title}: ${rating}`}
        >
          <span className={`font-bold tabular-nums ${rating.length > 2 ? 'text-base' : 'text-3xl'}`}>{rating}</span>
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold">{title}</div>
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">{periodLabel}</div>
          <p className="text-sm text-muted-foreground">{statement}</p>
        </div>
      </CardContent>
    </Card>
  )
}

interface PerformanceRatingCardsProps {
  ratingYtd: string
  ratingYtdColor: string
  ratingYtdHex?: string | null
  statementYtd: string
  ratingMonth: string
  ratingMonthColor: string
  ratingMonthHex?: string | null
  statementMonth: string
  /** Name of the month the "Month" rating covers, e.g. "July". */
  monthLabel?: string
}

export function PerformanceRatingCards({
  ratingYtd,
  ratingYtdColor,
  ratingYtdHex,
  statementYtd,
  ratingMonth,
  ratingMonthColor,
  ratingMonthHex,
  statementMonth,
  monthLabel,
}: PerformanceRatingCardsProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <RatingCard
        title="Supplier rating"
        periodLabel="Year to date"
        rating={ratingYtd}
        colorKey={ratingYtdColor}
        hex={ratingYtdHex}
        statement={statementYtd}
      />
      <RatingCard
        title="Supplier rating"
        periodLabel={monthLabel ? `Month · ${monthLabel}` : 'Month'}
        rating={ratingMonth}
        colorKey={ratingMonthColor}
        hex={ratingMonthHex}
        statement={statementMonth}
      />
    </div>
  )
}
